import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Clock, Landmark, TriangleAlert } from 'lucide-react';
import { useFetchAdvanceCodSummary, useRequestAdvanceCodActivation } from '@/hooksApi';
import { AdvanceCodRequestStatus } from '@/types';

/** Eligibility criteria bullet (DESIGN visual system). */
function Criteria() {
  const { t } = useTranslation();
  return (
    <div className='mt-5 rounded-xl border border-solid border-[#EBEBE6] bg-[#FAFAF7] px-4 py-3.5'>
      <div className='text-[11.5px] font-bold uppercase tracking-[0.4px] text-[#9A9A92]'>
        {String(t('advance_cod_promo_criteria_title'))}
      </div>
      <ul className='m-0 mt-2 flex list-none flex-col gap-1.5 p-0'>
        {['advance_cod_promo_criteria_1', 'advance_cod_promo_criteria_2'].map((key) => (
          <li key={key} className='flex items-center gap-2 text-[13.5px] text-[#4A4A44]'>
            <CheckCircle2 className='h-[15px] w-[15px] shrink-0 text-[#15794B]' strokeWidth={2.2} />
            {String(t(key))}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Advance COD activation promo (R1) — shown on the Finance Balance tab to
 * merchants NOT yet enrolled (no `user.advance_cod`). Explains the program and
 * offers a "Request activation" CTA hitting POST /merchants/advance-cod/request.
 *
 * After requesting, the response drives the surface (idempotent backend, safe
 * to re-render): requested/forwarded/overridden → "Request under review";
 * ineligible → friendly not-eligible copy + the criteria.
 *
 * Gate: the caller only mounts this for FINANCE-permissioned, non-enrolled
 * merchants. A 403 on the underlying advance-cod calls (a sub-user lacking the
 * advance_cod permission) hides the promo entirely rather than erroring.
 */
function AdvanceCodPromoCard() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useFetchAdvanceCodSummary();
  const { mutate, isLoading: submitting } = useRequestAdvanceCodActivation();

  const [localStatus, setLocalStatus] = useState<AdvanceCodRequestStatus | null>(null);
  const [failed, setFailed] = useState(false);
  const [hidden, setHidden] = useState(false);

  // Hide on any summary failure — a 403 (FINANCE sub-user without the
  // advance_cod permission) must degrade to nothing, never an error surface.
  if (hidden || isError || isLoading) return null;

  const status =
    localStatus ?? ((data?.object?.request_status as AdvanceCodRequestStatus | null) || null);

  const onRequest = () => {
    setFailed(false);
    mutate(undefined, {
      onSuccess: (res) => setLocalStatus(res.object.status),
      onError: (err) => {
        const httpStatus = (err as { response?: { status?: number } })?.response?.status;
        if (httpStatus === 403) setHidden(true);
        else setFailed(true);
      },
    });
  };

  const underReview = status === 'requested' || status === 'forwarded' || status === 'overridden';
  const ineligible = status === 'ineligible';

  return (
    <div
      className='rounded-2xl border border-solid border-[#EBEBE6] bg-white px-7 py-6'
      data-testid='advance-cod-promo'
    >
      <div className='flex items-center gap-3'>
        <span className='inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[rgba(237,224,74,0.22)]'>
          <Landmark className='h-[22px] w-[22px] text-[#1B1B18]' strokeWidth={2} />
        </span>
        <div>
          <div className='text-[18px] font-extrabold tracking-[-0.3px] text-[#1B1B18]'>
            {String(t('advance_cod_promo_title'))}
          </div>
          <div className='mt-0.5 text-[13.5px] leading-[1.5] text-[#9A9A92]'>
            {String(t('advance_cod_promo_body'))}
          </div>
        </div>
      </div>

      {underReview ? (
        <div
          className='mt-5 flex items-start gap-2.5 rounded-xl border border-solid border-[#CDE7D8] bg-[#E7F1EA] px-4 py-3.5'
          data-testid='advance-cod-promo-under-review'
        >
          <Clock className='mt-0.5 h-4 w-4 shrink-0 text-[#15794B]' strokeWidth={2.2} />
          <div>
            <div className='text-[14px] font-bold text-[#15794B]'>
              {String(t('advance_cod_promo_under_review_title'))}
            </div>
            <div className='mt-0.5 text-[13px] leading-[1.5] text-[#3F6B54]'>
              {String(t('advance_cod_promo_under_review_body'))}
            </div>
          </div>
        </div>
      ) : ineligible ? (
        <>
          <div
            className='mt-5 flex items-start gap-2.5 rounded-xl border border-solid border-[#F1DFAF] bg-[#FCF3DD] px-4 py-3.5'
            data-testid='advance-cod-promo-ineligible'
          >
            <TriangleAlert className='mt-0.5 h-4 w-4 shrink-0 text-[#B7791F]' strokeWidth={2.2} />
            <div>
              <div className='text-[14px] font-bold text-[#8A5A12]'>
                {String(t('advance_cod_promo_ineligible_title'))}
              </div>
              <div className='mt-0.5 text-[13px] leading-[1.5] text-[#8A6A2E]'>
                {String(t('advance_cod_promo_ineligible_body'))}
              </div>
            </div>
          </div>
          <Criteria />
        </>
      ) : (
        <>
          <Criteria />
          {failed ? (
            <div className='mt-3 text-[12.5px] font-semibold text-[#C0392B]'>
              {String(t('advance_cod_promo_error'))}
            </div>
          ) : null}
          <button
            type='button'
            onClick={onRequest}
            disabled={submitting}
            className='mt-5 inline-flex items-center gap-2 rounded-[11px] border-0 bg-[rgb(237,224,74)] px-5 py-3 text-[14.5px] font-bold text-[#1B1B18] transition-colors hover:bg-[rgb(230,217,60)] disabled:cursor-not-allowed disabled:opacity-60'
          >
            {submitting
              ? String(t('advance_cod_promo_requesting'))
              : String(t('advance_cod_promo_request'))}
          </button>
        </>
      )}
    </div>
  );
}

export default AdvanceCodPromoCard;
